import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import TextField from 'ui/elements/form/TextField';
import { halfSpacing, quarterSpacing } from 'ui/theme/themeConstants';
import CheckmarkIcon from 'ui/elements/icons/CheckmarkIcon';
import CloseIcon from 'ui/elements/icons/CloseIcon';
import IconButton from 'ui/elements/icons/IconButton';

const Container = styled.div`
  padding: ${halfSpacing};
  min-width: 200px;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${quarterSpacing};
`;

interface TextFilterInMenuProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  onClose?: () => void;
}

export default function TextFilterInMenu({
  value = '',
  onChange,
  placeholder,
  autoFocus = true,
  onClose,
}: TextFilterInMenuProps) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    // Update parent immediately so HeaderPopMenu tracks the changes
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleApply = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation();
    if (onChange) {
      onChange(localValue);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalValue('');
    if (onChange) {
      onChange('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply(e);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClear(e as any);
    }
  };

  return (
    <Container
      onClick={e => {
        // Prevent menu from closing when clicking in the container
        e.stopPropagation();
      }}
    >
      <InputWrapper>
        <TextField
          autoFocus={autoFocus}
          placeholder={placeholder || 'Filter...'}
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onMouseDown={e => {
            // Prevent menu from closing when clicking in the text field
            e.stopPropagation();
          }}
          onClick={e => {
            // Prevent menu from closing when clicking in the text field
            e.stopPropagation();
          }}
        />
        <IconButton
          color="blue"
          onClick={handleApply}
          sx={{
            '&:hover': {
              bgcolor: 'transparent',
            },
          }}
        >
          <CheckmarkIcon fontSize="small" />
        </IconButton>
        <IconButton
          color="grey"
          onClick={handleClear}
          sx={{
            '&:hover': {
              bgcolor: 'transparent',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </InputWrapper>
    </Container>
  );
}
